/**
 * VulnerabilityDetail — the per-vuln body, rendered as a child
 * route under [AnalysisLayout](./AnalysisLayout.jsx).
 *
 * Phase 7 — visual sweep. Outer layout + Back button still come
 * from Report.module.css (`detailBody` / `backButton`) since they're
 * shared with the AnalysisLayout chrome. Page-specific styling
 * (header card, section headers, body cards, CWE/Reference links)
 * lives in this file's own `VulnerabilityDetail.module.css` so
 * Report.module.css can drop its now-orphan `.vulnHeader*` /
 * `.vulnSeverityIconBox` / `.vulnViewDetails` rules.
 *
 * Severity now renders through the shared `<LevelBadge>` primitive
 * (filled chip with auto-contrast text) instead of the bespoke
 * shield-SVG-in-square. CWE + Reference links are class-driven
 * with CSS `:hover` — no more `onMouseEnter` / `onMouseLeave` event
 * handlers.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import LevelBadge from '../../components/ui/LevelBadge/LevelBadge';
import VulnerabilityManagementService from '../../services/VulnerabilityManagementService';
import { getSeverityColor, getCvssColor, getEpssColor } from '../../utils/severityStyle';
import ScoreCard from './ScoreCard';
import reportStyles from './Report.module.css';
import styles from './VulnerabilityDetail.module.css';

const SOURCE_NAMES = {
  NVD: 'National Vulnerability Database',
  GITHUB: 'GitHub Security Advisories',
  OSV: 'Open Source Vulnerabilities',
};

const detailUrlFor = (source, vulnId) => {
  switch (source?.toUpperCase()) {
    case 'NVD':    return `https://nvd.nist.gov/vuln/detail/${vulnId}`;
    case 'GITHUB': return `https://github.com/advisories/${vulnId}`;
    case 'OSV':    return `https://osv.dev/vulnerability/${vulnId}`;
    default:       return null;
  }
};

const inferSourceFromVulnId = (vulnId) => {
  if (vulnId?.startsWith('CVE-')) return 'NVD';
  if (vulnId?.startsWith('GHSA-')) return 'GITHUB';
  if (vulnId?.startsWith('OSV-')) return 'OSV';
  return 'NVD';
};

const formatDescriptionWithBackticks = (text) => {
  if (!text) return 'No description available.';
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return <em key={index} style={{ fontStyle: 'italic' }}>{part.slice(1, -1)}</em>;
    }
    return <span key={index}>{part}</span>;
  });
};

const VulnerabilityDetail = () => {
  const { uuid, vulnId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Outlet context comes from AnalysisLayout — analysisData is
  // already fetched, so this file no longer makes its own
  // getScanDetails call.
  useOutletContext(); // currently unused by the body; reserved for future cross-route access

  const [vulnerabilityData, setVulnerabilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Source: navigation state when arriving from the table; falls
  // back to the vuln-id prefix on direct URL / refresh.
  const source = location.state?.source || inferSourceFromVulnId(vulnId);

  useEffect(() => {
    if (!source || !vulnId) return;
    let cancelled = false;
    const fetchVuln = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await VulnerabilityManagementService.getVulnerabilityDetail(source, vulnId);
        if (!cancelled) setVulnerabilityData(data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching vulnerability details:', err);
        setError(err.message || 'Failed to load vulnerability details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchVuln();
    return () => {
      cancelled = true;
    };
  }, [source, vulnId]);

  const handleBack = () => {
    navigate(`/athena/vulnerability-management/analysis/${uuid}?tab=vulnerabilities`);
  };

  const BackButton = (
    <button type="button" className={reportStyles.backButton} onClick={handleBack}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="15 18 9 12 15 6" />
      </svg>
      Back to vulnerabilities
    </button>
  );

  if (loading) {
    return (
      <div className={reportStyles.detailBody}>
        {BackButton}
        <div className={reportStyles.statusMessage}>Loading vulnerability details...</div>
      </div>
    );
  }

  if (error || !vulnerabilityData) {
    return (
      <div className={reportStyles.detailBody}>
        {BackButton}
        <div className={reportStyles.statusError}>
          Error: {error || 'Failed to load vulnerability details'}
        </div>
      </div>
    );
  }

  const sev = vulnerabilityData.severity?.toUpperCase();
  const sevColor = getSeverityColor(sev);
  const sevLabel = sev ? sev.charAt(0) + sev.slice(1).toLowerCase() : 'Unknown';
  const sourceName = SOURCE_NAMES[source?.toUpperCase()] || source;
  const detailUrl = detailUrlFor(source, vulnId);

  return (
    <div className={reportStyles.detailBody}>
      {BackButton}

      {/* Vulnerability Header Card */}
      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderMain}>
          <div className={styles.detailHeaderInfo}>
            <div>
              <div className={styles.detailHeaderTitle}>
                {vulnId}
                <span className={styles.detailHeaderSource}>({source})</span>
              </div>
              <div className={styles.detailHeaderSubtitle}>{sourceName}</div>
            </div>
          </div>
          <div className={styles.detailHeaderSeverity}>
            <LevelBadge color={sevColor} label={`${sevLabel} Severity`} size="md" />
          </div>
        </div>
        {detailUrl && (
          <a
            href={detailUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.detailViewLink}
          >
            <span>View details in {sourceName}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </a>
        )}
      </div>

      {/* Overview */}
      <section>
        <h3 className={styles.sectionHeader}>Overview</h3>
        <div className={styles.detailCard}>
          {vulnerabilityData.title && (
            <h4 className={styles.overviewTitle}>{vulnerabilityData.title}</h4>
          )}
          <p className={styles.overviewBody}>
            {formatDescriptionWithBackticks(vulnerabilityData.description)}
          </p>
          {vulnerabilityData.recommendation && (
            <div className={styles.recommendationGroup}>
              <h4 className={styles.recommendationTitle}>Recommendation</h4>
              <p className={styles.overviewBody}>{vulnerabilityData.recommendation}</p>
            </div>
          )}
        </div>
      </section>

      {/* Scores */}
      <section>
        <h3 className={styles.sectionHeader}>Scores</h3>
        <div className={styles.scoresGrid}>
          <ScoreCard label="CVSS Base Score"              value={vulnerabilityData.cvssV3BaseScore}              maxValue={10.0} colorFn={getCvssColor} />
          <ScoreCard label="CVSS Impact Subscore"         value={vulnerabilityData.cvssV3ImpactSubScore}         maxValue={10.0} colorFn={getCvssColor} />
          <ScoreCard label="CVSS Exploitability Subscore" value={vulnerabilityData.cvssV3ExploitabilitySubScore} maxValue={10.0} colorFn={getCvssColor} />
          <ScoreCard label="EPSS Score"                   value={vulnerabilityData.epssScore}                    maxValue={1.0}  colorFn={getEpssColor} />
          <ScoreCard label="EPSS Percentile"              value={vulnerabilityData.epssPercentile}               maxValue={1.0}  colorFn={getEpssColor} />
        </div>
      </section>

      {/* Weaknesses (CWEs) */}
      {vulnerabilityData.cwes && vulnerabilityData.cwes.length > 0 && (
        <section>
          <h3 className={styles.sectionHeader}>Weaknesses (CWEs)</h3>
          <div className={styles.detailCard}>
            <div className={styles.cweList}>
              {vulnerabilityData.cwes.map((cwe, index) => (
                <a
                  key={index}
                  href={`https://cwe.mitre.org/data/definitions/${cwe.cweId}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.cweLink}
                >
                  <span>CWE-{cwe.cweId}: {cwe.name}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* References */}
      {vulnerabilityData.references && (
        <section>
          <h3 className={styles.sectionHeader}>References</h3>
          <div className={styles.detailCard}>
            <ReactMarkdown
              components={{
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.refLink}
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {vulnerabilityData.references}
            </ReactMarkdown>
          </div>
        </section>
      )}
    </div>
  );
};

export default VulnerabilityDetail;
